/**
 * ProductSelectionDialog - World-class product selection modal
 * Displays AI-found products with elegant, scannable design
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, ExternalLink, Sparkles, FileText, AlertCircle, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ProductSearchResult } from '../services/llmService';

interface ProductSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: ProductSearchResult[];
  onSelect: (product: ProductSearchResult) => void;
  searchQuery: string;
}

/**
 * Determines match quality label and styling based on confidence
 */
function getMatchQuality(confidence: number): {
  label: string;
  className: string;
  icon: React.ReactNode;
} {
  if (confidence >= 0.85) {
    return {
      label: 'Best Match',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      icon: <Sparkles className="h-3 w-3" />,
    };
  }
  if (confidence >= 0.7) {
    return {
      label: 'Good Match',
      className: 'bg-blue-50 text-blue-700 border-blue-200',
      icon: <CheckCircle2 className="h-3 w-3" />,
    };
  }
  if (confidence >= 0.5) {
    return {
      label: 'Possible',
      className: 'bg-amber-50 text-amber-700 border-amber-200',
      icon: <AlertCircle className="h-3 w-3" />,
    };
  }
  return {
    label: 'Low Match',
    className: 'bg-gray-50 text-gray-600 border-gray-200',
    icon: <AlertCircle className="h-3 w-3" />,
  };
}

/**
 * Extracts domain name from URL for display
 */
function extractDomain(url: string): string {
  try {
    const domain = new URL(url).hostname.replace('www.', '');
    // Truncate long domains
    return domain.length > 25 ? domain.slice(0, 22) + '...' : domain;
  } catch {
    return '';
  }
}

/**
 * Cleans description by removing common boilerplate patterns
 */
function cleanDescription(description: string | null | undefined): string | null {
  if (!description) return null;

  // Common boilerplate patterns to filter out
  const boilerplatePatterns = [
    /^about\s+(us|company)/i,
    /contact\s+us/i,
    /©\s*copyright/i,
    /all\s+rights\s+reserved/i,
    /press\s+releases?/i,
    /careers?\s+at/i,
    /privacy\s+policy/i,
    /terms\s+(of\s+)?(service|use)/i,
    /cookie\s+policy/i,
    /trademark/i,
    /^\s*log\s+in/i,
    /sign\s+up\s+for/i,
  ];

  // Check if description is mostly boilerplate
  const hasBoilerplate = boilerplatePatterns.some(pattern => pattern.test(description));

  if (hasBoilerplate && description.length < 150) {
    return null;
  }

  // Clean up the description
  let cleaned = description
    .replace(/©\s*\d{4}[-–]\d{4}\s*[\w\s,]+\s*(Inc|LLC|Ltd|Corp)?\.?/gi, '')
    .replace(/\*The .+ is a trademark.+/gi, '')
    .replace(/ABOUT\s+[\w\s]+CONTACT\s+US.*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();

  // If cleaned is too short or empty, return null
  if (cleaned.length < 20) return null;

  // Truncate to reasonable length
  if (cleaned.length > 150) {
    cleaned = cleaned.slice(0, 147) + '...';
  }

  return cleaned;
}

/**
 * Properly capitalizes brand name
 */
function formatBrand(brand: string): string {
  if (!brand) return 'Unknown';

  // Known brand capitalizations
  const knownBrands: Record<string, string> = {
    savant: 'Savant',
    lutron: 'Lutron',
    crestron: 'Crestron',
    control4: 'Control4',
    sonos: 'Sonos',
    sony: 'Sony',
    samsung: 'Samsung',
    lg: 'LG',
    qsc: 'QSC',
    jbl: 'JBL',
    bose: 'Bose',
    shure: 'Shure',
    biamp: 'Biamp',
    extron: 'Extron',
    cisco: 'Cisco',
    polycom: 'Polycom',
    logitech: 'Logitech',
    epson: 'Epson',
    panasonic: 'Panasonic',
    araknis: 'Araknis',
    ubiquiti: 'Ubiquiti',
  };

  const lowerBrand = brand.toLowerCase();
  if (knownBrands[lowerBrand]) {
    return knownBrands[lowerBrand];
  }

  // Capitalize first letter of each word
  return brand
    .split(/[\s-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Formats category for display
 */
function formatCategory(category: string): string {
  return category
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function ProductSelectionDialog({
  open,
  onOpenChange,
  products,
  onSelect,
  searchQuery,
}: ProductSelectionDialogProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleSelect = (product: ProductSearchResult, index: number) => {
    setSelectedIndex(index);
    // Small delay to show selection state
    setTimeout(() => {
      onSelect(product);
      onOpenChange(false);
      setSelectedIndex(null);
    }, 150);
  };

  // Sort products by confidence (best matches first)
  const sortedProducts = [...products].sort((a, b) => b.confidence - a.confidence);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 py-5 border-b bg-gradient-to-b from-white to-gray-50/50">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-muted-foreground" />
            <div>
              <DialogTitle className="text-xl">Select Product</DialogTitle>
              <DialogDescription className="text-sm mt-0.5">
                Found {products.length} matches for "{searchQuery}"
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Product List */}
        <ScrollArea className="max-h-[60vh]">
          <div className="p-4 space-y-2">
            {sortedProducts.map((product, index) => {
              const matchQuality = getMatchQuality(product.confidence);
              const description = cleanDescription(product.basic.description);
              const sourceUrl = (product as any).source_url;
              const sourceDomain = sourceUrl ? extractDomain(sourceUrl) : null;
              const isSelected = selectedIndex === index;
              const isPDF = sourceUrl?.toLowerCase().includes('.pdf');

              return (
                <button
                  key={index}
                  onClick={() => handleSelect(product, index)}
                  className={cn(
                    'w-full text-left p-4 rounded-xl border transition-all duration-150',
                    'hover:border-gray-300 hover:bg-gray-50/50 hover:shadow-sm',
                    'focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300',
                    isSelected
                      ? 'border-primary/60 bg-primary/5 shadow-sm'
                      : 'border-gray-200 bg-white',
                  )}
                >
                  <div className="flex items-start gap-4">
                    {/* Left side - Main info */}
                    <div className="flex-1 min-w-0">
                      {/* Brand & Model */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          {formatBrand(product.basic.brand)}
                        </span>
                        <span className="text-muted-foreground/40">•</span>
                        <Badge variant="outline" className={cn('text-xs', matchQuality.className)}>
                          {matchQuality.icon}
                          <span className="ml-1">{matchQuality.label}</span>
                        </Badge>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mt-1 truncate">
                        {product.basic.model}
                      </h3>

                      {/* Part Number */}
                      {product.basic.partNumber && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Part: {product.basic.partNumber}
                        </p>
                      )}

                      {/* Description - only show if clean */}
                      {description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{description}</p>
                      )}

                      {/* Footer row with category and source */}
                      <div className="flex items-center gap-3 mt-3 flex-wrap">
                        <Badge variant="secondary" className="text-xs font-normal">
                          {formatCategory(product.basic.category)}
                        </Badge>

                        {sourceDomain && (
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            className={cn(
                              'inline-flex items-center gap-1.5 text-xs',
                              'text-muted-foreground hover:text-primary transition-colors',
                            )}
                          >
                            {isPDF ? (
                              <FileText className="h-3 w-3" />
                            ) : (
                              <ExternalLink className="h-3 w-3" />
                            )}
                            {sourceDomain}
                          </a>
                        )}

                        {/* Specs preview if available */}
                        {product.specifications?.power?.consumption && (
                          <span className="text-xs text-muted-foreground">
                            {product.specifications.power.consumption}W
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right side - Selection indicator */}
                    <div
                      className={cn(
                        'flex-shrink-0 w-6 h-6 rounded-full border-2 transition-all',
                        'flex items-center justify-center',
                        isSelected
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-300 group-hover:border-primary/50',
                      )}
                    >
                      {isSelected && <CheckCircle2 className="h-4 w-4" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50/50 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Click a product to select it</p>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
